import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { match as Match } from "react-router-dom";
import { Location } from "history";
import { injectIntl, IntlShape } from "react-intl";
import { TableChangeParams } from "../../../../components/Table/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import { EnumPaths } from "../../../../utils/navigation";
import history from "../../../../history";
import Table from "../../../../components/Table";
import Dropdown, {
  DropdownValues,
} from "../../../../components/Tailwind/Dropdown";
import images from "../../../../images";
import { getTranslation } from "../../../../utils/translation";
import {
  displayDayDDMMYYYY_HHMM,
  DEFAULT_LIMIT,
} from "../../../../utils/config";
import {
  Round,
  SearchableRoundsFilterInput,
} from "../../../../lib/ground-aws-graphql-core/models/Api/Inspection";
import Badge from "../../../../components/Tailwind/Badge";
import { getStatusBadgeElements } from "../../../../utils/round";

interface Props {
  intl: IntlShape;
  match: Match<{ cid: string }>;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListRounds = (props: Props): JSX.Element => {
  const { intl, location, match } = props;
  const [loading, setLoading] = useState(false);

  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listRounds = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.listRounds
  );

  const rounds = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.rounds.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.rounds.total
  );

  const setRound = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setRound
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  useEffect(() => {
    if (location?.state) {
      fetchData(location?.state?.pageIndex, true, location?.state?.limit);
    } else fetchData();
  }, []);

  const fetchData = (pageIndex = 0, loader = true, limit = DEFAULT_LIMIT) => {
    if (loader) setLoading(true);

    const filter: SearchableRoundsFilterInput = {};
    filter.building_id = { eq: match.params.cid };
    listRounds({
      offset: pageIndex * limit,
      limit,
      filter,
    }).finally(() => setLoading(false));
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit } = changeParams;
    setInitialParams(changeParams);
    fetchData(pageIndex, false, limit);
  };

  const handleEditRound = (round: Round) => {
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.ROUNDS}/${round.id}/${EnumPaths.EDIT_MODE}`;
    setRound(null);
    history.push(editLink);
  };

  const tableHead = [
    "page.inspection.round.form.label",
    "page.inspection.round.assigned_to",
    "general.created_at",
    "general.updated_at",
    "general.status",
    "general.actions",
  ];

  const tableBody = rounds.map((round: Round) => {
    const { id, status, created_at, updated_at, user, form } = round;
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.ROUNDS}/${id}/${EnumPaths.EDIT_MODE}`;

    const dropdownValues: DropdownValues[] =
      status !== "PENDING"
        ? [
            {
              id: "edit_inspection_round_key",
              label: "general.edit",
              icon: images.edit,
              link: editLink,
            },
          ]
        : [];

    const onClick =
      round.status !== "PENDING"
        ? {
            onCellClick: () => handleEditRound(round),
          }
        : {};

    const elements = [
      {
        element: getTranslation(form.title),
        ...onClick,
      },
      {
        element: `${user?.first_name} ${user?.last_name}`,
        ...onClick,
      },
      {
        element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
        ...onClick,
      },
      {
        element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
        ...onClick,
      },
      {
        element: (
          <Badge
            bgClassName={getStatusBadgeElements(status).backgroundColor}
            textClassName={getStatusBadgeElements(status).textColor}
            dataCy={`inspection-round-status-${status}`}
          >
            {intl.formatMessage({
              id: `page.inspection.round.status.${status}`,
            })}
          </Badge>
        ),
        ...onClick,
      },
      {
        element: (
          <Dropdown
            values={dropdownValues}
            dataCy={`inspection-round-dropdown-${id}`}
          />
        ),
      },
    ];

    return {
      id: round.id.toString(),
      rowElements: elements,
    };
  });

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: `page.list.inspection.rounds.document.title`,
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.inspection.rounds.title"
        nbOfResults={total}
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.inspection.rounds.empty"
        onChange={handleOnChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />
    </div>
  );
};

export default injectIntl(ListRounds);
